'use client';

import { Button } from '@zealot/ui/components/button';
import { Calendar } from '@zealot/ui/components/calendar';
import { Input } from '@zealot/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@zealot/ui/components/popover';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { format } from 'date-fns';
import { CalendarIcon, ClockIcon } from 'lucide-react';
import * as React from 'react';
import type { DateRange, Matcher } from 'react-day-picker';

const datePickerTriggerVariants = cva(
  'inline-flex w-full items-center justify-between gap-2 rounded-lg border border-input bg-field text-left transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 data-popup-open:border-ring data-popup-open:ring-2 data-popup-open:ring-ring/30 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      size: {
        sm: "h-7 px-2 text-xs [&_svg:not([class*='size-'])]:size-3",
        default: "h-9 px-3 text-sm md:text-xs [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 px-3 text-sm [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: { size: 'default' },
  },
);

// gloss
type DatePickerProps = {
  value?: Date | null;
  onValueChange?: (date: Date | null) => void;
  placeholder?: string;
  displayFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  defaultMonth?: Date;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  className?: string;
  hasToday?: boolean;
  isClearable?: boolean;
  hasTime?: boolean;
  timeStep?: number;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
} & VariantProps<typeof datePickerTriggerVariants>;

const buildDisabledMatchers = (minDate?: Date, maxDate?: Date): Matcher[] => {
  const matchers: Matcher[] = [];
  if (minDate) matchers.push({ before: minDate });
  if (maxDate) matchers.push({ after: maxDate });
  return matchers;
};

const padTime = (n: number) => n.toString().padStart(2, '0');

// gloss
const DatePicker = ({
  value,
  onValueChange,
  placeholder = 'Pick a date',
  displayFormat: displayFormatProp,
  minDate,
  maxDate,
  defaultMonth,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  size = 'default',
  isInvalid = false,
  className,
  hasToday = false,
  isClearable = false,
  hasTime = false,
  timeStep,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const displayFormat = displayFormatProp ?? (hasTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy');

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const handleSelect = React.useCallback(
    (date: Date | undefined) => {
      if (!date) {
        if (isClearable) {
          onValueChange?.(null);
          handleOpenChange(false);
        }
        return;
      }
      if (hasTime) {
        const newDate = new Date(date);
        const hours = value?.getHours() ?? 0;
        const minutes = value?.getMinutes() ?? 0;
        newDate.setHours(hours, minutes, 0, 0);
        onValueChange?.(newDate);
      } else {
        onValueChange?.(date ?? null);
        handleOpenChange(false);
      }
    },
    [onValueChange, hasTime, value, handleOpenChange, isClearable],
  );

  const handleTimeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const [hours, minutes] = e.target.value.split(':').map(Number);
      if (Number.isNaN(hours!) || Number.isNaN(minutes!)) return;
      const base = value ? new Date(value) : new Date();
      base.setHours(hours!, minutes!, 0, 0);
      onValueChange?.(base);
    },
    [onValueChange, value],
  );

  const handleTodayClick = React.useCallback(() => {
    onValueChange?.(new Date());
    if (!hasTime) handleOpenChange(false);
  }, [onValueChange, hasTime, handleOpenChange]);

  const handleClearClick = React.useCallback(() => {
    onValueChange?.(null);
    handleOpenChange(false);
  }, [onValueChange, handleOpenChange]);

  const disabledMatchers = React.useMemo(() => buildDisabledMatchers(minDate, maxDate), [minDate, maxDate]);

  const timeValue = value ? `${padTime(value.getHours())}:${padTime(value.getMinutes())}` : '';

  const hasFooter = hasToday || (isClearable && value);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        data-slot="date-picker-trigger"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={isInvalid || ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn(datePickerTriggerVariants({ size, className }))}
        render={<button type="button" />}
      >
        <span data-slot="date-picker-value" className={cn(!value && 'text-muted-foreground')}>
          {value ? format(value, displayFormat) : placeholder}
        </span>
        <CalendarIcon className="text-muted-foreground" />
      </PopoverTrigger>
      {}
      <PopoverContent align="start" className="pointer-events-auto w-auto p-0">
        <div data-slot="date-picker-content">
          <Calendar
            mode="single"
            selected={value ?? undefined}
            onSelect={handleSelect}
            defaultMonth={value ?? defaultMonth}
            disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
          />
          {hasTime && (
            <div data-slot="date-picker-time" className="border-border border-t px-3 py-2">
              <Input
                type="time"
                size="sm"
                step={timeStep}
                value={timeValue}
                onChange={handleTimeChange}
                startIcon={<ClockIcon />}
              />
            </div>
          )}
          {hasFooter && (
            <div
              data-slot="date-picker-footer"
              className="flex items-center justify-between border-border border-t px-3 py-2"
            >
              {hasToday ? (
                <Button variant="ghost" size="sm" onClick={handleTodayClick}>
                  Today
                </Button>
              ) : (
                <span />
              )}
              {isClearable && value && (
                <Button variant="ghost" size="sm" onClick={handleClearClick}>
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

type DateRangePickerProps = {
  value?: DateRange | null;
  onValueChange?: (range: DateRange | null) => void;
  placeholder?: string;
  displayFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  isInvalid?: boolean;
  className?: string;
  numberOfMonths?: number;
  hasTimeRange?: boolean;
  timeStep?: number;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
} & VariantProps<typeof datePickerTriggerVariants>;

// gloss
const DateRangePicker = ({
  value,
  onValueChange,
  placeholder = 'Pick a date range',
  displayFormat: displayFormatProp,
  minDate,
  maxDate,
  disabled = false,
  size = 'default',
  isInvalid = false,
  className,
  numberOfMonths = 2,
  hasTimeRange = false,
  timeStep,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const pendingRangeRef = React.useRef(false);
  const displayFormat = displayFormatProp ?? (hasTimeRange ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy');

  const handleOpenChange = React.useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) {
      pendingRangeRef.current = false;
    }
  }, []);

  const handleSelect = React.useCallback(
    (range: DateRange | undefined) => {
      if (!range) {
        onValueChange?.(null);
        return;
      }
      if (hasTimeRange) {
        const fromDate = range.from ? new Date(range.from) : undefined;
        const toDate = range.to ? new Date(range.to) : undefined;
        if (fromDate && value?.from) {
          fromDate.setHours(value.from.getHours(), value.from.getMinutes(), 0, 0);
        }
        if (toDate && value?.to) {
          toDate.setHours(value.to.getHours(), value.to.getMinutes(), 0, 0);
        }
        onValueChange?.({ from: fromDate!, to: toDate });
      } else {
        onValueChange?.(range ?? null);
      }
      if (range.from && !range.to) {
        pendingRangeRef.current = true;
      } else if (range.from && range.to && pendingRangeRef.current) {
        pendingRangeRef.current = false;
        if (!hasTimeRange) setIsOpen(false);
      }
    },
    [onValueChange, hasTimeRange, value],
  );

  const handleStartTimeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const [hours, minutes] = e.target.value.split(':').map(Number);
      if (Number.isNaN(hours!) || Number.isNaN(minutes!)) return;
      const updated = new Date(value?.from ?? new Date());
      updated.setHours(hours!, minutes!, 0, 0);
      onValueChange?.({ from: updated, to: value?.to });
    },
    [onValueChange, value],
  );

  const handleEndTimeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const [hours, minutes] = e.target.value.split(':').map(Number);
      if (Number.isNaN(hours!) || Number.isNaN(minutes!)) return;
      const updated = new Date(value?.to ?? value?.from ?? new Date());
      updated.setHours(hours!, minutes!, 0, 0);
      onValueChange?.({ from: value?.from ?? new Date(), to: updated });
    },
    [onValueChange, value],
  );

  const disabledMatchers = React.useMemo(() => buildDisabledMatchers(minDate, maxDate), [minDate, maxDate]);

  const startTimeValue = value?.from ? `${padTime(value.from.getHours())}:${padTime(value.from.getMinutes())}` : '';
  const endTimeValue = value?.to ? `${padTime(value.to.getHours())}:${padTime(value.to.getMinutes())}` : '';

  const formatRange = () => {
    if (!value?.from) return null;
    if (!value.to) return format(value.from, displayFormat);
    return `${format(value.from, displayFormat)} \u2013 ${format(value.to, displayFormat)}`;
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        data-slot="date-range-picker-trigger"
        id={id}
        disabled={disabled}
        aria-invalid={isInvalid || ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn(datePickerTriggerVariants({ size, className }))}
        render={<button type="button" />}
      >
        <span data-slot="date-range-picker-value" className={cn(!value?.from && 'text-muted-foreground')}>
          {formatRange() ?? placeholder}
        </span>
        <CalendarIcon className="text-muted-foreground" />
      </PopoverTrigger>
      {}
      <PopoverContent align="start" className="pointer-events-auto w-auto p-0">
        <div data-slot="date-range-picker-content">
          <Calendar
            mode="range"
            selected={value ?? undefined}
            onSelect={handleSelect}
            defaultMonth={value?.from ?? undefined}
            numberOfMonths={numberOfMonths}
            disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
          />
          {hasTimeRange && (
            <div data-slot="date-range-picker-time" className="grid grid-cols-2 gap-2 border-border border-t px-3 py-2">
              <Input
                type="time"
                size="sm"
                step={timeStep}
                value={startTimeValue}
                onChange={handleStartTimeChange}
                startIcon={<ClockIcon />}
                aria-label="Start time"
              />
              <Input
                type="time"
                size="sm"
                step={timeStep}
                value={endTimeValue}
                onChange={handleEndTimeChange}
                startIcon={<ClockIcon />}
                aria-label="End time"
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export type { DatePickerProps, DateRangePickerProps };
export { DatePicker, DateRangePicker, datePickerTriggerVariants };
